import React, {useEffect, useState} from 'react';
import {MDBCol, MDBRow} from "mdbreact";
import {Button, Container, Input} from "@nextui-org/react";
import {Drawer, Divider, Layout, Modal, Popconfirm, Table, Tabs} from "antd";
import {useCollection} from "react-firebase-hooks/firestore";
import {collection, deleteDoc, doc, getFirestore, serverTimestamp, updateDoc, addDoc, Timestamp} from "firebase/firestore";
import {ColumnsType} from "antd/lib/table";
import moment from 'moment';
import Firebase from "../../Firebase/Firebase";
import Headerbar from "../../navBars/Headerbar";
import Sidebar from "../../navBars/Sidebar";
import {Tag} from "primereact/tag";
import {getAuth} from "firebase/auth";
import {useToast} from "@chakra-ui/react";
import {query, where} from "firebase/firestore";
import AddContent from "../AppContent/AddContent";
import AddAdModal from "./AddAdModal";


const db = getFirestore(Firebase);
const auth = getAuth(Firebase);
const {Content} = Layout;
const {TabPane} = Tabs;

type StatusFilter = 'all' | 'pending' | 'awaiting_payment' | 'active' | 'rejected' | 'stopped';

interface RowType {
    key: string;
    sellerId: string;
    sellerName: string;
    type: string;
    productName: string;
    duration: number;
    weightTier: string;
    quotedPrice: number;
    paymentStatus: string;
    statusText: string;
    status: JSX.Element;
    submittedAt: string;
    submittedMs: number;
    bannerImageUrl: string;
    bannerLinkTarget: string;
    bannerLinkValue: string;
    raw: any;
}

const TYPE_LABELS: Record<string, string> = {
    homepage_featured: 'Homepage Featured',
    category_featured: 'Category Featured',
    search_boost: 'Search Boost',
    banner: 'Banner Ad',
    splash_ad: 'Splash Ad',
};

const STATUS_SEVERITY: Record<string, any> = {
    pending:         'warning',
    awaiting_payment:'info',
    active:          'success',
    approved:        'success',
    rejected:        'danger',
    stopped:         'danger',
    expired:         'secondary',
    cancelled:       'secondary',
};

// ─── Shared UI helpers ────────────────────────────────────────────────────────

const InfoRow = ({label, value}: {label: string; value: React.ReactNode}) => (
    <div style={{display:'flex', justifyContent:'space-between', alignItems:'center',
                 padding:'9px 0', borderBottom:'1px solid #f3f3f3'}}>
        <span style={{color:'#888', fontSize:13}}>{label}</span>
        <span style={{fontWeight:500, fontSize:13, maxWidth:'62%', textAlign:'right'}}>{value ?? '—'}</span>
    </div>
);

// ─── Promotion Drawer ─────────────────────────────────────────────────────────

const PromoDrawer = ({promo, open, onClose}: {promo: RowType | null; open: boolean; onClose: () => void}) => {
    const toast = useToast();
    const [busy, setBusy] = useState(false);

    // Quote state
    const [quotePrice, setQuotePrice]   = useState('');
    const [quoteNotes, setQuoteNotes]   = useState('');
    const [quoteOpen, setQuoteOpen]     = useState(false);

    // Cancel/stop state
    const [cancelOpen, setCancelOpen]   = useState(false);
    const [cancelNotes, setCancelNotes] = useState('');

    // Activate state (only when admin manually activates after confirming payment outside system)
    const [activateOpen, setActivateOpen]       = useState(false);
    const [activateBannerUrl, setActivateBannerUrl] = useState('');
    const [activateLinkValue, setActivateLinkValue] = useState('');
    const [activateWeight, setActivateWeight]   = useState('3');

    const adminUid = () => auth.currentUser?.uid ?? 'admin';

    if (!promo) return null;

    const r = promo.raw;
    const status = promo.statusText;
    const isBannerType  = r.promotionType === 'banner' || r.promotionType === 'splash_ad';
    const isProductType = ['homepage_featured', 'category_featured', 'search_boost'].includes(r.promotionType);
    const imageUrl      = r.bannerImageUrl || r.productImageUrl || '';
    const startDate     = r.startDate?.toDate ? moment(r.startDate.toDate()).format('DD MMM YYYY') : null;
    const endDate       = r.endDate?.toDate   ? moment(r.endDate.toDate()).format('DD MMM YYYY')   : null;
    const reviewedDate  = r.reviewedAt?.toDate ? moment(r.reviewedAt.toDate()).format('DD MMM YYYY HH:mm') : null;

    // ── Action handlers ──

    const doQuote = async () => {
        const price = parseFloat(quotePrice);
        if (isNaN(price) || price <= 0) {
            toast({position:'top', title:'Enter a valid price', status:'error', duration:2000, isClosable:true});
            return;
        }
        setBusy(true);
        try {
            await updateDoc(doc(db, 'promotion_requests', promo.key), {
                status: 'awaiting_payment',
                quotedPrice: price,
                ...(quoteNotes.trim() ? {adminNotes: quoteNotes.trim()} : {}),
                reviewedAt: serverTimestamp(),
                reviewedBy: adminUid(),
            });
            toast({position:'top', title:'Price quoted — awaiting seller payment', status:'success', duration:2500, isClosable:true});
            setQuoteOpen(false);
            onClose();
        } catch (e: any) {
            toast({position:'top', title: e.message, status:'error', duration:3000, isClosable:true});
        } finally { setBusy(false); }
    };

    const doReject = async () => {
        setBusy(true);
        try {
            await updateDoc(doc(db, 'promotion_requests', promo.key), {
                status: 'rejected',
                ...(cancelNotes.trim() ? {adminNotes: cancelNotes.trim()} : {}),
                reviewedAt: serverTimestamp(),
                reviewedBy: adminUid(),
            });
            toast({position:'top', title:'Request rejected', status:'success', duration:2000, isClosable:true});
            setCancelOpen(false);
            onClose();
        } catch (e: any) {
            toast({position:'top', title: e.message, status:'error', duration:3000, isClosable:true});
        } finally { setBusy(false); }
    };

    const doStop = async () => {
        setBusy(true);
        try {
            // Deactivate the serving ad doc if present
            if (r.adId) {
                const adCol = r.promotionType === 'splash_ad' ? 'splashAds'
                    : r.promotionType === 'banner' ? 'banners' : 'productAds';
                await updateDoc(doc(db, adCol, r.adId), {isActive: false, stoppedAt: serverTimestamp()});
            }
            await updateDoc(doc(db, 'promotion_requests', promo.key), {
                status: 'stopped',
                ...(cancelNotes.trim() ? {adminNotes: cancelNotes.trim()} : {}),
                stoppedAt: serverTimestamp(),
                reviewedBy: adminUid(),
            });
            toast({position:'top', title:'Promotion stopped', status:'success', duration:2000, isClosable:true});
            setCancelOpen(false);
            onClose();
        } catch (e: any) {
            toast({position:'top', title: e.message, status:'error', duration:3000, isClosable:true});
        } finally { setBusy(false); }
    };

    const doActivate = async () => {
        setBusy(true);
        const durationDays: number = r.durationDays ?? 7;
        const end = new Date();
        end.setDate(end.getDate() + durationDays);
        const weight = parseInt(activateWeight) || 3;
        try {
            let adId: string | null = null;
            if (isBannerType) {
                const colName = r.promotionType === 'splash_ad' ? 'splashAds' : 'banners';
                const bannerUrl = activateBannerUrl || r.bannerImageUrl || '';
                const adDoc: any = {imageUrl: bannerUrl, isActive: true, weight,
                    isPersistent: false, promotionRequestId: promo.key, endDate: Timestamp.fromDate(end)};
                if (r.promotionType === 'banner') {
                    const lt = r.bannerLinkTarget ?? 'product';
                    adDoc.linkType = lt === 'shop' ? 'shop' : lt === 'external_url' ? 'url' : 'product';
                    adDoc.linkValue = activateLinkValue || r.bannerExternalUrl || '';
                    adDoc.clickCount = 0; adDoc.impressionCount = 0;
                }
                const ref = await addDoc(collection(db, colName), adDoc);
                adId = ref.id;
            } else if (isProductType && r.productId) {
                const ref = await addDoc(collection(db, 'productAds'), {
                    productId: r.productId, sellerId: r.sellerId, promotionType: r.promotionType,
                    isActive: true, weight, isPersistent: false, promotionRequestId: promo.key,
                    endDate: Timestamp.fromDate(end), clickCount: 0, impressionCount: 0,
                });
                adId = ref.id;
            }
            await updateDoc(doc(db, 'promotion_requests', promo.key), {
                status: 'active',
                ...(adId ? {adId} : {}),
                startDate: serverTimestamp(),
                endDate: Timestamp.fromDate(end),
                reviewedAt: serverTimestamp(),
                reviewedBy: adminUid(),
            });
            toast({position:'top', title:'Promotion activated!', status:'success', duration:2000, isClosable:true});
            setActivateOpen(false);
            onClose();
        } catch (e: any) {
            toast({position:'top', title: e.message, status:'error', duration:3000, isClosable:true});
        } finally { setBusy(false); }
    };

    const cancelModalTitle = status === 'active' ? 'Stop Promotion' : 'Reject / Cancel';
    const cancelOkText     = status === 'active' ? 'Stop' : 'Reject';

    return (
        <>
        <Drawer
            open={open}
            onClose={onClose}
            width={820}
            title={null}
            bodyStyle={{padding:0, background:'#f7f8fc'}}
        >
            {/* ── Header ── */}
            <div style={{background:'#fff', padding:'20px 24px', borderBottom:'1px solid #eee'}}>
                <div style={{display:'flex', alignItems:'center', gap:10, marginBottom:6}}>
                    <span style={{fontSize:12, color:'#888'}}>Request ID</span>
                    <span style={{fontFamily:'monospace', fontSize:11, color:'#555'}}>{promo.key}</span>
                </div>
                <div style={{display:'flex', alignItems:'center', gap:14, flexWrap:'wrap'}}>
                    <span style={{fontSize:22, fontWeight:700, color:'#111'}}>{promo.type}</span>
                    <Tag severity={STATUS_SEVERITY[status]} value={status.replace('_', ' ').toUpperCase()} />
                    {promo.paymentStatus === 'paid' && <Tag severity="success" value="Payment confirmed" />}
                    {promo.paymentStatus !== 'paid' && status === 'awaiting_payment' &&
                        <Tag severity="warning" value="Awaiting payment" />}
                    {promo.quotedPrice > 0 &&
                        <span style={{fontSize:18, fontWeight:600, color:'#29327b'}}>
                            MWK {promo.quotedPrice.toLocaleString()}
                        </span>}
                </div>
            </div>

            <div className="promo-drawer-body" style={{display:'flex', height:'calc(100vh - 120px)', overflow:'hidden'}}>

                {/* ── Left panel ── */}
                <div className="promo-drawer-side" style={{width:270, minWidth:270, background:'#fff', borderRight:'1px solid #eee',
                              padding:'16px 18px', overflowY:'auto'}}>

                    {/* Image preview */}
                    {imageUrl ? (
                        <div style={{marginBottom:14}}>
                            <img src={imageUrl} alt="" style={{
                                width:'100%', maxHeight:160, objectFit:'cover',
                                borderRadius:10, border:'1px solid #eee'
                            }} />
                        </div>
                    ) : (
                        <div style={{width:'100%', height:100, background:'#f5f5f5', borderRadius:10,
                                     display:'flex', alignItems:'center', justifyContent:'center',
                                     color:'#ccc', fontSize:13, marginBottom:14}}>
                            No image
                        </div>
                    )}

                    <InfoRow label="Seller"    value={promo.sellerName} />
                    <InfoRow label="Type"      value={promo.type} />
                    {promo.productName && <InfoRow label="Product" value={promo.productName} />}
                    <InfoRow label="Duration"  value={`${promo.duration} days`} />
                    <InfoRow label="Tier"      value={promo.weightTier} />
                    {r.weight && <InfoRow label="Weight"   value={r.weight} />}
                    <InfoRow label="Quoted"    value={promo.quotedPrice > 0 ? `MWK ${promo.quotedPrice.toLocaleString()}` : '—'} />
                    <InfoRow label="Payment"   value={
                        <Tag severity={promo.paymentStatus === 'paid' ? 'success' : 'warning'}
                             value={promo.paymentStatus} />
                    } />
                    <InfoRow label="Submitted" value={promo.submittedAt} />
                    {startDate && <InfoRow label="Started"   value={startDate} />}
                    {endDate   && <InfoRow label="Ends"      value={endDate} />}
                    {r.adminNotes && (
                        <>
                            <Divider style={{margin:'10px 0 6px'}} />
                            <div style={{fontSize:12, color:'#888', marginBottom:4}}>Admin notes</div>
                            <div style={{fontSize:13, color:'#555', background:'#fafafa',
                                         borderRadius:6, padding:'8px 10px', border:'1px solid #eee'}}>
                                {r.adminNotes}
                            </div>
                        </>
                    )}
                </div>

                {/* ── Right panel ── */}
                <div style={{flex:1, overflowY:'auto', padding:'0 4px'}}>
                    <Tabs defaultActiveKey="details" style={{padding:'0 16px'}}>

                        {/* Details tab */}
                        <TabPane tab="Details" key="details">
                            <div style={{padding:'12px 0'}}>

                                {/* Product type info */}
                                {isProductType && (
                                    <>
                                        <div style={{fontWeight:600, marginBottom:10, color:'#333'}}>Product Details</div>
                                        <div style={{background:'#fff', borderRadius:10, border:'1px solid #eee',
                                                     padding:'12px 16px', marginBottom:14}}>
                                            {r.productImageUrl && (
                                                <img src={r.productImageUrl} alt="" style={{
                                                    width:80, height:80, objectFit:'cover',
                                                    borderRadius:8, marginBottom:10, display:'block'
                                                }} />
                                            )}
                                            <InfoRow label="Product"  value={r.productName ?? '—'} />
                                            <InfoRow label="Product ID" value={
                                                <span style={{fontFamily:'monospace', fontSize:11}}>{r.productId ?? '—'}</span>
                                            } />
                                            <InfoRow label="Promo type" value={TYPE_LABELS[r.promotionType] ?? r.promotionType} />
                                            {r.promotionType === 'category_featured' && r.bannerCategoryId && (
                                                <InfoRow label="Category ID" value={r.bannerCategoryId} />
                                            )}
                                        </div>
                                    </>
                                )}

                                {/* Banner / splash info */}
                                {isBannerType && (
                                    <>
                                        <div style={{fontWeight:600, marginBottom:10, color:'#333'}}>
                                            {r.promotionType === 'splash_ad' ? 'Splash Ad' : 'Banner'} Details
                                        </div>
                                        <div style={{background:'#fff', borderRadius:10, border:'1px solid #eee',
                                                     padding:'12px 16px', marginBottom:14}}>
                                            {r.bannerImageUrl && (
                                                <img src={r.bannerImageUrl} alt="Banner preview" style={{
                                                    width:'100%', maxHeight:220, objectFit:'cover',
                                                    borderRadius:8, marginBottom:12
                                                }} />
                                            )}
                                            {r.promotionType === 'banner' && <>
                                                <InfoRow label="Placement" value={r.bannerPlacement ?? '—'} />
                                                <InfoRow label="Link target" value={r.bannerLinkTarget ?? '—'} />
                                                {r.bannerLinkTarget === 'external_url' &&
                                                    <InfoRow label="URL" value={
                                                        <a href={r.bannerExternalUrl} target="_blank" rel="noreferrer"
                                                           style={{color:'#29327b', wordBreak:'break-all', fontSize:11}}>
                                                            {r.bannerExternalUrl}
                                                        </a>
                                                    } />
                                                }
                                                {r.bannerHeadlineText &&
                                                    <InfoRow label="Headline" value={r.bannerHeadlineText} />}
                                                {r.bannerCategoryId &&
                                                    <InfoRow label="Category ID" value={r.bannerCategoryId} />}
                                            </>}
                                        </div>
                                    </>
                                )}

                                {/* Metadata */}
                                <Divider style={{margin:'6px 0 10px'}}>Request Info</Divider>
                                <div style={{background:'#fff', borderRadius:10, border:'1px solid #eee', padding:'12px 16px'}}>
                                    <InfoRow label="Request ID" value={
                                        <span style={{fontFamily:'monospace', fontSize:11}}>{promo.key}</span>
                                    } />
                                    <InfoRow label="Seller ID" value={
                                        <span style={{fontFamily:'monospace', fontSize:11}}>{r.sellerId}</span>
                                    } />
                                    {r.adId && <InfoRow label="Ad ID" value={
                                        <span style={{fontFamily:'monospace', fontSize:11}}>{r.adId}</span>
                                    } />}
                                    {reviewedDate && <InfoRow label="Reviewed at" value={reviewedDate} />}
                                    <InfoRow label="Payment ref" value={r.paymentRef ?? '—'} />
                                </div>
                            </div>
                        </TabPane>

                        {/* Actions tab */}
                        <TabPane tab="Actions" key="actions">
                            <div style={{padding:'12px 0'}}>
                                <div style={{fontWeight:600, marginBottom:12, color:'#333'}}>Current Status</div>
                                <div style={{background:'#fff', borderRadius:10, border:'1px solid #eee',
                                             padding:'12px 16px', marginBottom:18}}>
                                    <InfoRow label="Status" value={
                                        <Tag severity={STATUS_SEVERITY[status]}
                                             value={status.replace('_', ' ')} />
                                    } />
                                    <InfoRow label="Payment" value={
                                        <Tag severity={promo.paymentStatus === 'paid' ? 'success' : 'warning'}
                                             value={promo.paymentStatus} />
                                    } />
                                    {promo.quotedPrice > 0 &&
                                        <InfoRow label="Quoted price"
                                                 value={`MWK ${promo.quotedPrice.toLocaleString()}`} />}
                                    {startDate && <InfoRow label="Active since" value={startDate} />}
                                    {endDate   && <InfoRow label="Ends"         value={endDate} />}
                                </div>

                                {/* ── pending: Quote + Reject ── */}
                                {status === 'pending' && (
                                    <>
                                        <div style={{fontWeight:600, marginBottom:8, color:'#333'}}>Actions</div>
                                        <div style={{display:'flex', gap:10, flexWrap:'wrap'}}>
                                            <Button size="sm" color="primary"
                                                    onPress={() => { setQuotePrice(promo.quotedPrice > 0 ? String(promo.quotedPrice) : ''); setQuoteNotes(''); setQuoteOpen(true); }}>
                                                Quote Price
                                            </Button>
                                            <Button size="sm" color="error" flat
                                                    onPress={() => { setCancelNotes(''); setCancelOpen(true); }}>
                                                Reject
                                            </Button>
                                        </div>
                                    </>
                                )}

                                {/* ── awaiting_payment: Reject only (no activate) ── */}
                                {status === 'awaiting_payment' && (
                                    <>
                                        <div style={{fontWeight:600, marginBottom:8, color:'#333'}}>Actions</div>
                                        <div style={{background:'#fef3c7', borderRadius:8, padding:'10px 14px',
                                                     marginBottom:12, fontSize:13, color:'#92400e'}}>
                                            Waiting for the seller to confirm payment (MWK {promo.quotedPrice.toLocaleString()}).
                                            Activation happens automatically once payment is confirmed.
                                        </div>
                                        <div style={{display:'flex', gap:10, flexWrap:'wrap'}}>
                                            <Button size="sm" color="error" flat
                                                    onPress={() => { setCancelNotes(''); setCancelOpen(true); }}>
                                                Reject / Cancel
                                            </Button>
                                        </div>
                                    </>
                                )}

                                {/* ── active: Stop ── */}
                                {status === 'active' && (
                                    <>
                                        <div style={{fontWeight:600, marginBottom:8, color:'#333'}}>Actions</div>
                                        <div style={{display:'flex', gap:10, flexWrap:'wrap'}}>
                                            <Button size="sm" color="error" flat
                                                    onPress={() => { setCancelNotes(''); setCancelOpen(true); }}>
                                                Stop Promotion
                                            </Button>
                                        </div>
                                    </>
                                )}

                                {/* ── terminal states ── */}
                                {['rejected', 'stopped', 'expired', 'cancelled'].includes(status) && (
                                    <div style={{color:'#aaa', fontSize:13, marginTop:8}}>
                                        This promotion is {status}. No further actions available.
                                    </div>
                                )}
                            </div>
                        </TabPane>

                    </Tabs>
                </div>
            </div>
        </Drawer>

        {/* Quote modal */}
        <Modal title="Quote Price" open={quoteOpen} onOk={doQuote} onCancel={() => setQuoteOpen(false)}
               okText="Send Quote" confirmLoading={busy}>
            <div className="my-2">
                <p style={{marginBottom:8, fontSize:13}}>
                    <b>{promo.sellerName}</b> — {promo.type} ({promo.duration}d, {promo.weightTier})
                </p>
                <label style={{display:'block', marginBottom:4, fontSize:13}}>Price (MWK)</label>
                <Input type="number" placeholder="e.g. 5000" value={quotePrice}
                       onChange={(e: any) => setQuotePrice(e.target.value)} fullWidth />
                <label style={{display:'block', margin:'12px 0 4px', fontSize:13}}>Notes for seller (optional)</label>
                <Input placeholder="Any notes..." value={quoteNotes}
                       onChange={(e: any) => setQuoteNotes(e.target.value)} fullWidth />
            </div>
        </Modal>

        {/* Reject / Stop modal */}
        <Modal title={cancelModalTitle} open={cancelOpen}
               onOk={status === 'active' ? doStop : doReject}
               onCancel={() => setCancelOpen(false)}
               okText={cancelOkText} okButtonProps={{danger: true}} confirmLoading={busy}>
            <div className="my-2">
                <p style={{marginBottom:8, fontSize:13}}>
                    {status === 'active'
                        ? <>Stop <b>{promo.sellerName}</b>'s active {promo.type} promotion?</>
                        : <>Reject <b>{promo.sellerName}</b>'s {promo.type} request?</>}
                </p>
                <label style={{display:'block', marginBottom:4, fontSize:13}}>Reason / notes (optional)</label>
                <Input placeholder="Reason..." value={cancelNotes}
                       onChange={(e: any) => setCancelNotes(e.target.value)} fullWidth />
            </div>
        </Modal>

        {/* Activate modal — only reachable from admin override, kept for future use */}
        <Modal title="Activate Promotion" open={activateOpen} onOk={doActivate}
               onCancel={() => setActivateOpen(false)} okText="Activate"
               okButtonProps={{style:{background:'#22c55e', borderColor:'#22c55e'}}}
               confirmLoading={busy}>
            <div className="my-2">
                <p style={{marginBottom:8, fontSize:13}}>
                    <b>{promo.sellerName}</b> — {promo.type}
                </p>
                {isBannerType && <>
                    <label style={{display:'block', marginBottom:4, fontSize:13}}>Banner Image URL</label>
                    <Input placeholder="https://..." value={activateBannerUrl}
                           onChange={(e: any) => setActivateBannerUrl(e.target.value)} fullWidth />
                    {r.promotionType === 'banner' && <>
                        <label style={{display:'block', margin:'12px 0 4px', fontSize:13}}>Link value</label>
                        <Input placeholder="URL / product ID / shop ID" value={activateLinkValue}
                               onChange={(e: any) => setActivateLinkValue(e.target.value)} fullWidth />
                    </>}
                </>}
                <label style={{display:'block', margin:'12px 0 4px', fontSize:13}}>Weight (1–10)</label>
                <Input type="number" placeholder="3" value={activateWeight}
                       onChange={(e: any) => setActivateWeight(e.target.value)} fullWidth />
            </div>
        </Modal>
        </>
    );
};

// ─── Live Ads tab ─────────────────────────────────────────────────────────────
// Shows what is *actually serving* in the app — every doc in `banners` and
// `splashAds`, regardless of whether it came through the seller promotion flow or
// was added directly in App Content. This is the single place to stop/remove a
// running ad that has no promotion_requests entry.

interface LiveAdRow {
    key: string;
    col: 'banners' | 'splashAds';
    kind: string;              // 'Banner' | 'Splash Ad'
    imageUrl: string;
    source: 'Seller promotion' | 'Admin-added';
    promotionRequestId: string | null;
    isActive: boolean;
    endsAt: string;
    raw: any;
}

const LiveAds = () => {
    const toast = useToast();
    const [bannerSnap]  = useCollection(collection(db, 'banners'),
        {snapshotListenOptions: {includeMetadataChanges: true}});
    const [splashSnap]  = useCollection(collection(db, 'splashAds'),
        {snapshotListenOptions: {includeMetadataChanges: true}});
    const [rows, setRows] = useState<LiveAdRow[]>([]);
    const [busyKey, setBusyKey] = useState<string | null>(null);
    const [selected, setSelected] = useState<LiveAdRow | null>(null);
    const [drawerOpen, setDrawerOpen] = useState(false);
    // Rich admin ad-creation (mirrors the seller form, minus payment/approval),
    // so banners/splash ads are added in the same place they're managed.
    const [addOpen, setAddOpen] = useState(false);
    const [addType, setAddType] = useState<'banner' | 'splash'>('banner');

    const fmtTs = (v: any): string | null =>
        v?.toDate ? moment(v.toDate()).format('DD MMM YYYY HH:mm')
            : (typeof v === 'string' ? v : null);

    useEffect(() => {
        const build = (snap: any, col: 'banners' | 'splashAds', kind: string): LiveAdRow[] =>
            (snap?.docs ?? []).map((d: any) => {
                const r = d.data();
                const end = r.endDate?.toDate ? moment(r.endDate.toDate()) : null;
                return {
                    key: d.id,
                    col,
                    kind,
                    imageUrl: r.imageUrl ?? '',
                    source: r.promotionRequestId ? 'Seller promotion' : 'Admin-added',
                    promotionRequestId: r.promotionRequestId ?? null,
                    isActive: r.isActive !== false,   // admin banners have no isActive → treat as active
                    endsAt: end ? end.format('DD MMM YYYY') : '—',
                    raw: r,
                } as LiveAdRow;
            });
        setRows([
            ...build(bannerSnap, 'banners', 'Banner'),
            ...build(splashSnap, 'splashAds', 'Splash Ad'),
        ]);
    }, [bannerSnap, splashSnap]);

    const stopAd = async (row: LiveAdRow) => {
        setBusyKey(row.key);
        try {
            await updateDoc(doc(db, row.col, row.key), {isActive: false, stoppedAt: serverTimestamp()});
            if (row.promotionRequestId) {
                await updateDoc(doc(db, 'promotion_requests', row.promotionRequestId),
                    {status: 'stopped', stoppedAt: serverTimestamp()});
            }
            toast({position:'top', title:'Ad stopped', status:'success', duration:2000, isClosable:true});
            if (selected?.key === row.key) { setDrawerOpen(false); setSelected(null); }
        } catch (e: any) {
            toast({position:'top', title: e.message, status:'error', duration:3000, isClosable:true});
        } finally { setBusyKey(null); }
    };

    const deleteAd = async (row: LiveAdRow) => {
        setBusyKey(row.key);
        try {
            await deleteDoc(doc(db, row.col, row.key));
            if (row.promotionRequestId) {
                await updateDoc(doc(db, 'promotion_requests', row.promotionRequestId),
                    {status: 'stopped', stoppedAt: serverTimestamp()}).catch(() => {});
            }
            toast({position:'top', title:'Ad deleted', status:'success', duration:2000, isClosable:true});
            if (selected?.key === row.key) { setDrawerOpen(false); setSelected(null); }
        } catch (e: any) {
            toast({position:'top', title: e.message, status:'error', duration:3000, isClosable:true});
        } finally { setBusyKey(null); }
    };

    const columns: ColumnsType<LiveAdRow> = [
        {
            title: 'Preview', key: 'preview',
            render: (_: any, row: LiveAdRow) => (
                row.imageUrl
                    ? <img src={row.imageUrl} alt="" style={{width:64, height:40, objectFit:'cover',
                            borderRadius:6, border:'1px solid #eee'}} />
                    : <div style={{width:64, height:40, background:'#f5f5f5', borderRadius:6}} />
            ),
        },
        {title: 'Type', dataIndex: 'kind', key: 'kind',
            filters: [{text:'Banner', value:'Banner'}, {text:'Splash Ad', value:'Splash Ad'}],
            onFilter: (v: any, r: LiveAdRow) => r.kind === v},
        {
            title: 'Source', dataIndex: 'source', key: 'source',
            filters: [{text:'Seller promotion', value:'Seller promotion'}, {text:'Admin-added', value:'Admin-added'}],
            onFilter: (v: any, r: LiveAdRow) => r.source === v,
            render: (s: string) => <Tag severity={s === 'Seller promotion' ? 'info' : 'secondary'} value={s} />,
        },
        {
            title: 'Status', dataIndex: 'isActive', key: 'isActive',
            render: (a: boolean) => <Tag severity={a ? 'success' : 'danger'} value={a ? 'active' : 'stopped'} />,
        },
        {title: 'Ends', dataIndex: 'endsAt', key: 'endsAt'},
        {
            title: 'Actions', key: 'actions',
            render: (_: any, row: LiveAdRow) => (
                // Stop click bubbling so the action buttons don't also open the drawer.
                <div style={{display:'flex', gap:8}} onClick={(e) => e.stopPropagation()}>
                    {row.isActive && (
                        <Popconfirm title="Stop this ad? It will stop serving in the app."
                                    okText="Stop" okButtonProps={{danger:true}}
                                    onConfirm={() => stopAd(row)}>
                            <Button size="xs" color="warning" flat disabled={busyKey === row.key}>Stop</Button>
                        </Popconfirm>
                    )}
                    <Popconfirm title="Delete this ad permanently?"
                                okText="Delete" okButtonProps={{danger:true}}
                                onConfirm={() => deleteAd(row)}>
                        <Button size="xs" color="error" flat disabled={busyKey === row.key}>Delete</Button>
                    </Popconfirm>
                </div>
            ),
        },
    ];

    const r = selected?.raw ?? {};
    const isBanner = selected?.kind === 'Banner';

    return (
        <>
        <div style={{display:'flex', justifyContent:'flex-end', gap:10, margin:'4px 0 0'}}>
            <Button size="sm" color="primary" auto
                    onPress={() => { setAddType('banner'); setAddOpen(true); }}>
                <i className="pi pi-plus" style={{marginRight:6}}/> Add Banner
            </Button>
            <Button size="sm" color="secondary" auto
                    onPress={() => { setAddType('splash'); setAddOpen(true); }}>
                <i className="pi pi-plus" style={{marginRight:6}}/> Add Splash Ad
            </Button>
        </div>

        <div style={{background:'#fff', borderRadius:12, border:'1px solid #eee',
                     boxShadow:'0 1px 4px rgba(0,0,0,.06)', overflow:'hidden', marginTop:8}}>
            <Table
                columns={columns}
                dataSource={rows}
                rowKey="key"
                size="middle"
                onRow={(record) => ({
                    onClick: () => { setSelected(record); setDrawerOpen(true); },
                    style: {cursor: 'pointer'},
                })}
                pagination={{defaultPageSize: 20, showSizeChanger: true,
                              showTotal: (t: number, r: number[]) => `${r[0]}–${r[1]} of ${t}`}}
            />
        </div>

        {/* Rich admin house-ad creation */}
        <AddAdModal open={addOpen} type={addType} onClose={() => setAddOpen(false)} />

        {/* ── Live ad details drawer ── */}
        <Drawer
            open={drawerOpen}
            onClose={() => { setDrawerOpen(false); setSelected(null); }}
            width={560}
            title={null}
            bodyStyle={{padding:0, background:'#f7f8fc'}}
        >
            {selected && (
                <>
                    {/* Header */}
                    <div style={{background:'#fff', padding:'20px 24px', borderBottom:'1px solid #eee'}}>
                        <div style={{display:'flex', alignItems:'center', gap:10, marginBottom:6}}>
                            <span style={{fontSize:12, color:'#888'}}>Ad ID</span>
                            <span style={{fontFamily:'monospace', fontSize:11, color:'#555'}}>{selected.key}</span>
                        </div>
                        <div style={{display:'flex', alignItems:'center', gap:14, flexWrap:'wrap'}}>
                            <span style={{fontSize:22, fontWeight:700, color:'#111'}}>{selected.kind}</span>
                            <Tag severity={selected.isActive ? 'success' : 'danger'}
                                 value={selected.isActive ? 'ACTIVE' : 'STOPPED'} />
                            <Tag severity={selected.source === 'Seller promotion' ? 'info' : 'secondary'}
                                 value={selected.source} />
                        </div>
                    </div>

                    <div style={{padding:'16px 24px'}}>
                        {/* Image preview */}
                        {selected.imageUrl ? (
                            <img src={selected.imageUrl} alt="" style={{width:'100%', maxHeight:220,
                                objectFit:'cover', borderRadius:10, border:'1px solid #eee', marginBottom:16}} />
                        ) : (
                            <div style={{width:'100%', height:120, background:'#f5f5f5', borderRadius:10,
                                         display:'flex', alignItems:'center', justifyContent:'center',
                                         color:'#ccc', fontSize:13, marginBottom:16}}>No image</div>
                        )}

                        <div style={{background:'#fff', borderRadius:10, border:'1px solid #eee', padding:'12px 16px'}}>
                            <InfoRow label="Type"   value={selected.kind} />
                            <InfoRow label="Source" value={selected.source} />
                            <InfoRow label="Status" value={
                                <Tag severity={selected.isActive ? 'success' : 'danger'}
                                     value={selected.isActive ? 'active' : 'stopped'} />
                            } />
                            {r.title && <InfoRow label="Title" value={r.title} />}
                            {r.headlineText && <InfoRow label="Headline" value={r.headlineText} />}
                            {r.placement && <InfoRow label="Placement"
                                value={r.placement === 'home' ? 'Home page' : 'Category page'} />}
                            {r.weightTier && <InfoRow label="Priority"
                                value={r.weightTier.charAt(0).toUpperCase() + r.weightTier.slice(1)} />}
                            {r.weight != null && <InfoRow label="Weight" value={r.weight} />}
                            <InfoRow label="Persistent" value={r.isPersistent ? 'Yes' : 'No'} />
                            <InfoRow label="Ends" value={selected.endsAt} />
                            {fmtTs(r.createdAt) && <InfoRow label="Created"    value={fmtTs(r.createdAt)} />}
                            {fmtTs(r.stoppedAt) && <InfoRow label="Stopped at" value={fmtTs(r.stoppedAt)} />}

                            {isBanner && <>
                                {r.linkType  && <InfoRow label="Link type"  value={r.linkType} />}
                                {r.linkValue && <InfoRow label="Link value" value={
                                    <span style={{wordBreak:'break-all', fontSize:12}}>{r.linkValue}</span>
                                } />}
                                <InfoRow label="Clicks"      value={r.clickCount ?? 0} />
                                <InfoRow label="Impressions" value={r.impressionCount ?? 0} />
                            </>}

                            <InfoRow label="Ad ID" value={
                                <span style={{fontFamily:'monospace', fontSize:11}}>{selected.key}</span>
                            } />
                            {selected.promotionRequestId && <InfoRow label="Request ID" value={
                                <span style={{fontFamily:'monospace', fontSize:11}}>{selected.promotionRequestId}</span>
                            } />}
                        </div>

                        {/* Actions */}
                        <div style={{fontWeight:600, margin:'18px 0 8px', color:'#333'}}>Actions</div>
                        <div style={{display:'flex', gap:10, flexWrap:'wrap'}}>
                            {selected.isActive && (
                                <Popconfirm title="Stop this ad? It will stop serving in the app."
                                            okText="Stop" okButtonProps={{danger:true}}
                                            onConfirm={() => stopAd(selected)}>
                                    <Button size="sm" color="warning" flat disabled={busyKey === selected.key}>
                                        Stop Ad
                                    </Button>
                                </Popconfirm>
                            )}
                            <Popconfirm title="Delete this ad permanently?"
                                        okText="Delete" okButtonProps={{danger:true}}
                                        onConfirm={() => deleteAd(selected)}>
                                <Button size="sm" color="error" flat disabled={busyKey === selected.key}>
                                    Delete Ad
                                </Button>
                            </Popconfirm>
                        </div>
                    </div>
                </>
            )}
        </Drawer>
        </>
    );
};

// ─── Featured Products tab ────────────────────────────────────────────────────
// Featuring a product is a merchandising/promo action, so it lives here alongside
// the ads rather than on the Catalog page. Reads `featured_products` and resolves
// product + seller info; adds via the shared AddContent modal.

const FeaturedProducts = () => {
    const toast = useToast();
    const [featuredSnap] = useCollection(collection(db, 'featured_products'),
        {snapshotListenOptions: {includeMetadataChanges: true}});
    const [productSnap]  = useCollection(collection(db, 'products'),
        {snapshotListenOptions: {includeMetadataChanges: true}});
    const [sellerSnap]   = useCollection(
        query(collection(db, 'users'), where('type', '==', 'Seller')),
        {snapshotListenOptions: {includeMetadataChanges: true}});

    const [addOpen, setAddOpen] = useState(false);

    const products = (productSnap?.docs ?? []).map((d: any) => d.data());
    const sellers  = (sellerSnap?.docs ?? []).map((d: any) => d.data());
    const featured = (featuredSnap?.docs ?? []).map((d: any) => ({...d.data(), key: d.id}));

    const removeFeatured = async (key: string) => {
        try {
            await deleteDoc(doc(db, 'featured_products', key));
            toast({position:'top', title:'Removed from featured', status:'success', duration:2000, isClosable:true});
        } catch (e: any) {
            toast({position:'top', title: e.message, status:'error', duration:3000, isClosable:true});
        }
    };

    return (
        <>
            <div style={{display:'flex', justifyContent:'flex-end', margin:'4px 0 0'}}>
                <Button size="sm" color="primary" auto onPress={() => setAddOpen(true)}>
                    <i className="pi pi-plus" style={{marginRight:6}}/> Feature a Product
                </Button>
            </div>

            <div style={{display:'grid', gap:14, marginTop:12,
                         gridTemplateColumns:'repeat(auto-fill, minmax(190px, 1fr))'}}>
                {featured.map((f: any) => {
                    const prod = products.find((p: any) => p.productId === f.productId);
                    const img = prod?.imageUrl ?? prod?.images?.[0] ?? prod?.image ?? prod?.imageURL;
                    const seller = sellers.find((s: any) => s.Uid === prod?.sellerId || s.sellerID === prod?.sellerId);
                    return (
                        <div key={f.key} style={{background:'#fff', borderRadius:12, border:'1px solid #eee',
                                     boxShadow:'0 1px 4px rgba(0,0,0,.05)', overflow:'hidden'}}>
                            {img ? (
                                <img src={img} alt={prod?.name ?? f.productId}
                                     style={{width:'100%', height:130, objectFit:'cover'}} />
                            ) : (
                                <div style={{height:130, background:'#f5f5f5', display:'flex',
                                             alignItems:'center', justifyContent:'center', color:'#ccc'}}>No image</div>
                            )}
                            <div style={{padding:'10px 12px'}}>
                                <div style={{fontWeight:600, fontSize:13, whiteSpace:'nowrap',
                                             overflow:'hidden', textOverflow:'ellipsis'}}>
                                    {prod?.name ?? <span style={{color:'#aaa'}}>Unknown product</span>}
                                </div>
                                {seller && <div style={{fontSize:12, color:'#888', marginTop:2}}>{seller.fullName}</div>}
                                <div style={{display:'flex', justifyContent:'flex-end', marginTop:8}}>
                                    <Popconfirm title="Remove from featured products?"
                                                okText="Remove" okButtonProps={{danger:true}}
                                                onConfirm={() => removeFeatured(f.key)}>
                                        <Button size="xs" color="error" flat>Remove</Button>
                                    </Popconfirm>
                                </div>
                            </div>
                        </div>
                    );
                })}
                {featured.length === 0 && (
                    <div style={{gridColumn:'1/-1', textAlign:'center', color:'#aaa',
                                 fontSize:13, padding:'28px 0'}}>
                        No featured products yet. Click “Feature a Product” to add one.
                    </div>
                )}
            </div>

            <AddContent contentType="featured_products" openDrawer={addOpen}
                        closeDrawer={setAddOpen} products={products} parentCategory="" />
        </>
    );
};

// ─── Main Promotions page ─────────────────────────────────────────────────────

const Promotions = () => {
    const [collapsed, setCollapsed] = useState(false);
    const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
    const [allRows, setAllRows] = useState<RowType[]>([]);
    const [dataArray, setDataArray] = useState<RowType[]>([]);

    const [drawerOpen, setDrawerOpen] = useState(false);
    const [selectedPromo, setSelectedPromo] = useState<RowType | null>(null);

    const [snapshot, loading] = useCollection(
        collection(db, 'promotion_requests'),
        {snapshotListenOptions: {includeMetadataChanges: true}}
    );
    const [userSnapshot] = useCollection(
        collection(db, 'users'),
        {snapshotListenOptions: {includeMetadataChanges: true}}
    );

    const columns: ColumnsType<RowType> = [
        {
            title: 'Seller', dataIndex: 'sellerName', key: 'sellerName',
            sorter: (a: RowType, b: RowType) => a.sellerName.localeCompare(b.sellerName),
        },
        {
            title: 'Type', dataIndex: 'type', key: 'type',
        },
        {
            title: 'Product / Banner',
            key: 'preview',
            render: (_: any, row: RowType) => (
                <div style={{display:'flex', alignItems:'center', gap:8}}>
                    {(row.bannerImageUrl || row.raw.productImageUrl) ? (
                        <img src={row.bannerImageUrl || row.raw.productImageUrl} alt=""
                             style={{width:36, height:36, objectFit:'cover', borderRadius:6, border:'1px solid #eee'}} />
                    ) : (
                        <div style={{width:36, height:36, background:'#f5f5f5', borderRadius:6,
                                     display:'flex', alignItems:'center', justifyContent:'center', color:'#ccc', fontSize:12}}>
                            —
                        </div>
                    )}
                    <span style={{fontSize:13}}>{row.productName || <span style={{color:'#aaa'}}>—</span>}</span>
                </div>
            ),
        },
        {
            title: 'Duration', dataIndex: 'duration', key: 'duration',
            render: (d: number) => `${d}d`,
        },
        {
            title: 'Tier', dataIndex: 'weightTier', key: 'weightTier',
        },
        {
            title: 'Quoted (MWK)', dataIndex: 'quotedPrice', key: 'quotedPrice',
            render: (p: number) => p > 0 ? p.toLocaleString() : <span style={{color:'#aaa'}}>—</span>,
            sorter: (a: RowType, b: RowType) => a.quotedPrice - b.quotedPrice,
        },
        {
            title: 'Payment', dataIndex: 'paymentStatus', key: 'paymentStatus',
            render: (t: string) => <Tag severity={t === 'paid' ? 'success' : 'warning'} value={t} />,
        },
        {
            title: 'Status', dataIndex: 'status', key: 'status',
        },
        {
            title: 'Submitted', dataIndex: 'submittedAt', key: 'submittedAt',
            sorter: (a: RowType, b: RowType) => a.submittedMs - b.submittedMs,
            defaultSortOrder: 'descend' as const,
        },
    ];

    useEffect(() => {
        const users: any[] = [];
        userSnapshot?.docs.forEach((d: any) => users.push({id: d.id, ...d.data()}));

        const rows: RowType[] = [];
        snapshot?.docs.forEach((d: any) => {
            const r = d.data();
            const user = users.find(u => u.id === r.sellerId || u.Uid === r.sellerId);
            const sellerName = user?.fullName ?? user?.Fullname ?? r.sellerId;
            const statusText: string = r.status ?? 'pending';
            const severity = STATUS_SEVERITY[statusText] ?? 'info';
            const m = r.submittedAt?.toDate ? moment(r.submittedAt.toDate()) : null;
            rows.push({
                key: d.id,
                sellerId: r.sellerId,
                sellerName,
                type: TYPE_LABELS[r.promotionType] ?? r.promotionType,
                productName: r.productName ?? '',
                duration: r.durationDays ?? 7,
                weightTier: r.weightTier ?? 'standard',
                quotedPrice: r.quotedPrice ?? 0,
                paymentStatus: r.paymentStatus ?? 'unpaid',
                statusText,
                status: <Tag severity={severity} value={statusText.replace('_', ' ')} />,
                submittedAt: m ? m.format('DD MMM YYYY HH:mm') : '',
                submittedMs: m ? m.valueOf() : 0,
                bannerImageUrl: r.bannerImageUrl ?? '',
                bannerLinkTarget: r.bannerLinkTarget ?? '',
                bannerLinkValue: r.bannerExternalUrl ?? r.bannerLinkValue ?? '',
                raw: r,
            });
        });

        rows.sort((a, b) => b.submittedMs - a.submittedMs);
        setAllRows(rows);
    }, [snapshot, userSnapshot]);

    useEffect(() => {
        setDataArray(statusFilter === 'all' ? allRows : allRows.filter(r => r.statusText === statusFilter));
    }, [allRows, statusFilter]);

    const filterButtons: {label: string; value: StatusFilter}[] = [
        {label: 'All',             value: 'all'},
        {label: 'Pending',         value: 'pending'},
        {label: 'Awaiting Payment',value: 'awaiting_payment'},
        {label: 'Active',          value: 'active'},
        {label: 'Stopped',         value: 'stopped'},
        {label: 'Rejected',        value: 'rejected'},
    ];

    return (
        <>
            <Layout style={{minHeight: '100vh'}}>
                <Headerbar collapsed={collapsed} setCollapsed={setCollapsed}/>
                <Layout className="site-layout">
                    <Sidebar collapsed={collapsed}/>
                    <Content>
                        <Container className="mt-2">
                            <Tabs defaultActiveKey="requests">
                                <TabPane tab="Requests" key="requests">
                                    <MDBRow>
                                        <MDBCol>
                                            <div className="page-header">
                                                <h4 className="page-title">
                                                    Promotions
                                                    <span className="page-title-count">({dataArray.length})</span>
                                                </h4>
                                                <div style={{display:'flex', gap:6, flexWrap:'wrap'}}>
                                                    {filterButtons.map(f => (
                                                        <button key={f.value}
                                                            className={`filter-chip${statusFilter === f.value ? ' active' : ''}`}
                                                            onClick={() => setStatusFilter(f.value)}>
                                                            {f.label}
                                                            {` (${f.value === 'all' ? allRows.length : allRows.filter(r => r.statusText === f.value).length})`}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        </MDBCol>
                                    </MDBRow>
                                    <MDBRow>
                                        <MDBCol>
                                            <div style={{background:'#fff', borderRadius:12, border:'1px solid #eee',
                                                         boxShadow:'0 1px 4px rgba(0,0,0,.06)', overflow:'hidden', marginTop:8}}>
                                                <Table
                                                    columns={columns}
                                                    dataSource={dataArray}
                                                    loading={loading}
                                                    rowKey="key"
                                                    size="middle"
                                                    onRow={(record) => ({
                                                        onClick: () => { setSelectedPromo(record); setDrawerOpen(true); },
                                                        style: {cursor: 'pointer'},
                                                    })}
                                                    pagination={{defaultPageSize: 20, showSizeChanger: true,
                                                                  showTotal: (t: number, r: number[]) => `${r[0]}–${r[1]} of ${t}`}}
                                                />
                                            </div>
                                        </MDBCol>
                                    </MDBRow>
                                </TabPane>

                                <TabPane tab="Live Ads" key="liveAds">
                                    <MDBRow>
                                        <MDBCol>
                                            <div className="page-header">
                                                <h4 className="page-title">Currently Serving</h4>
                                                <span style={{color:'#888', fontSize:13}}>
                                                    Every banner &amp; splash ad live in the app — seller promotions and admin-added.
                                                </span>
                                            </div>
                                            <LiveAds />
                                        </MDBCol>
                                    </MDBRow>
                                </TabPane>

                                <TabPane tab="Featured Products" key="featured">
                                    <MDBRow>
                                        <MDBCol>
                                            <div className="page-header">
                                                <h4 className="page-title">Featured Products</h4>
                                                <span style={{color:'#888', fontSize:13}}>
                                                    Hand-picked products surfaced on the app home screen.
                                                </span>
                                            </div>
                                            <FeaturedProducts />
                                        </MDBCol>
                                    </MDBRow>
                                </TabPane>
                            </Tabs>
                        </Container>
                    </Content>
                </Layout>
            </Layout>

            <PromoDrawer
                promo={selectedPromo}
                open={drawerOpen}
                onClose={() => { setDrawerOpen(false); setSelectedPromo(null); }}
            />
        </>
    );
};

export default Promotions;
